// ConclaveAdminPage.js - Partie 1
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/ConclaveAdminPage.css';

// Composants SVG inline
const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconNextRound = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 17 18 12 13 7"></polyline>
    <polyline points="6 17 11 12 6 7"></polyline>
  </svg>
);

const IconPause = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16"></rect>
    <rect x="14" y="4" width="4" height="16"></rect>
  </svg>
);

const IconResume = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

const IconReset = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v6h6"></path>
    <path d="M21 12A9 9 0 0 0 6 5.3L3 8"></path>
    <path d="M21 22v-6h-6"></path>
    <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7"></path>
  </svg>
);

const IconEmptyVotes = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18"></path>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const IconCrown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
  </svg>
);

const ConclaveAdminPage = () => {
  const [conclaveState, setConclaveState] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [currentVotes, setCurrentVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editState, setEditState] = useState({});
  
  // Fonction pour charger l'état du conclave
  const fetchConclaveData = async () => {
    try {
      // Charger l'état du conclave
      const stateResponse = await axios.get('/api/conclave/state');
      setConclaveState(stateResponse.data.conclaveState);
      setRounds(stateResponse.data.rounds);
      setEditState(stateResponse.data.conclaveState);
      
      // Charger les votes actuels
      const votesResponse = await axios.get('/api/conclave/current-votes');
      setCurrentVotes(votesResponse.data.results);
      
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des données du conclave:', err);
      setError('Erreur lors du chargement des données. Veuillez réessayer.');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchConclaveData();
    
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchConclaveData, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Passer au tour suivant
  const advanceToNextRound = async () => {
    try {
      await axios.post('/api/conclave/admin/next-round');
      toast.success('Passage au tour suivant effectué avec succès');
      fetchConclaveData();
    } catch (err) {
      console.error('Erreur lors du passage au tour suivant:', err);
      toast.error('Erreur lors du passage au tour suivant');
    }
  };
  
  // Mettre en pause le conclave
  const pauseConclave = async () => {
    try {
      let pauseReason = '';
      let exhortationBy = '';
      
      if (conclaveState.totalRounds === 13) {
        pauseReason = 'Pause après 3 jours sans résultat (13 scrutins)';
        exhortationBy = 'le premier cardinal de l\'ordre des diacres';
      } else if (conclaveState.totalRounds === 21) {
        pauseReason = 'Pause après 7 nouveaux scrutins sans résultat';
        exhortationBy = 'le premier cardinal de l\'ordre des prêtres';
      } else if (conclaveState.totalRounds === 29) {
        pauseReason = 'Pause après 7 nouveaux scrutins sans résultat';
        exhortationBy = 'le premier cardinal de l\'ordre des évêques';
      } else {
        pauseReason = 'Pause administrative';
        exhortationBy = '';
      }
      
      await axios.post('/api/conclave/admin/toggle-pause', {
        pause: true,
        reason: pauseReason,
        exhortationBy: exhortationBy
      });
      
      toast.info(`Le conclave a été mis en pause. Raison: ${pauseReason}`);
      fetchConclaveData();
    } catch (err) {
      console.error('Erreur lors de la mise en pause du conclave:', err);
      toast.error('Erreur lors de la mise en pause du conclave');
    }
  };
  
  // Reprendre le conclave
  const resumeConclave = async () => {
    try {
      await axios.post('/api/conclave/admin/toggle-pause', {
        pause: false
      });
      
      toast.success('Le conclave a repris');
      fetchConclaveData();
    } catch (err) {
      console.error('Erreur lors de la reprise du conclave:', err);
      toast.error('Erreur lors de la reprise du conclave');
    }
  };
  
  // Élire un pape
  const electPope = async (cardinalId) => {
    try {
      await axios.post('/api/conclave/admin/elect-pope', {
        cardinalId
      });
      
      const electedCardinal = currentVotes.find(v => v.cardinal._id === cardinalId);
      toast.success(`${electedCardinal.cardinal.nomComplet} a été élu pape !`);
      fetchConclaveData();
    } catch (err) {
      console.error('Erreur lors de l\'élection du pape:', err);
      toast.error('Erreur lors de l\'élection du pape');
    }
  };
  
  // Réinitialiser le conclave
  const resetConclave = async () => {
    if (window.confirm('ATTENTION: Ceci réinitialisera complètement le conclave et supprimera tous les votes et historiques. Cette action est irréversible. Êtes-vous sûr de vouloir continuer ?')) {
      try {
        await axios.post('/api/conclave/admin/reset');
        toast.success('Le conclave a été complètement réinitialisé');
        fetchConclaveData();
      } catch (err) {
        console.error('Erreur lors de la réinitialisation du conclave:', err);
        toast.error('Erreur lors de la réinitialisation du conclave');
      }
    }
  };
  
  // Gérer le changement dans le formulaire d'édition
  const handleEditChange = (e) => {
    const { name, value, type } = e.target;
    setEditState({
      ...editState,
      [name]: type === 'number' ? parseInt(value, 10) : value
    });
  };
  
  // Sauvegarder les modifications
  const saveChanges = async () => {
    try {
      await axios.put('/api/conclave/admin/state', editState);
      setConclaveState(editState);
      setEditMode(false);
      toast.success('Modifications enregistrées avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement des modifications:', err);
      toast.error('Erreur lors de l\'enregistrement des modifications');
    }
  };
  
  // Annuler les modifications
  const cancelEdit = () => {
    setEditState(conclaveState);
    setEditMode(false);
  };
  
  // Formatter la date
  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  if (loading) return <div className="loading">Chargement de l'interface d'administration...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!conclaveState) return <div className="loading">Initialisation...</div>;
  return (
    <div className="conclave-admin-page">
      <h1>Administration du Conclave Virtuel</h1>
      
      <div className="admin-panels">
        <div className="conclave-status-panel">
          <div className="panel-header">
            <h2>État Actuel du Conclave</h2>
            <button 
              className="edit-button"
              onClick={() => setEditMode(!editMode)}
              title={editMode ? "Annuler l'édition" : "Modifier les paramètres"}
            >
              {editMode ? "Annuler" : <IconEdit />}
            </button>
          </div>
          
          {editMode ? (
            <div className="edit-form">
              <div className="form-row">
                <label>Date de début:</label>
                <input 
                  type="datetime-local" 
                  name="startDate" 
                  value={editState.startDate ? new Date(editState.startDate).toISOString().slice(0, 16) : ''} 
                  onChange={handleEditChange} 
                />
              </div>
              
              <div className="form-row">
                <label>Jour actuel:</label>
                <input 
                  type="number" 
                  name="currentDay" 
                  value={editState.currentDay} 
                  onChange={handleEditChange} 
                  min="1"
                />
              </div>
              
              <div className="form-row">
                <label>Tour actuel:</label>
                <input 
                  type="number" 
                  name="currentRound" 
                  value={editState.currentRound} 
                  onChange={handleEditChange} 
                  min="1"
                />
              </div>
              
              <div className="form-row">
                <label>Total des tours:</label>
                <input 
                  type="number" 
                  name="totalRounds" 
                  value={editState.totalRounds} 
                  onChange={handleEditChange} 
                  min="1"
                />
              </div>
              
              <div className="form-row">
                <label>Période du jour:</label>
                <select 
                  name="dayPeriod" 
                  value={editState.dayPeriod} 
                  onChange={handleEditChange}
                >
                  <option value="morning">Matin</option>
                  <option value="afternoon">Après-midi</option>
                </select>
              </div>
              
              <div className="form-row">
                <label>Nombre d'électeurs:</label>
                <input 
                  type="number" 
                  name="electors" 
                  value={editState.electors} 
                  onChange={handleEditChange} 
                  min="1"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  className="save-button"
                  onClick={saveChanges}
                >
                  Enregistrer les modifications
                </button>
                <button 
                  className="cancel-button"
                  onClick={cancelEdit}
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="status-details">
              <div className="status-row">
                <span className="status-label">Début du conclave:</span>
                <span className="status-value">{formatDate(conclaveState.startDate)}</span>
              </div>
              
              <div className="status-row">
                <span className="status-label">État:</span>
                <span className={`status-value status-badge ${conclaveState.isActive ? (conclaveState.isPaused ? 'paused' : 'active') : 'completed'}`}>
                  {conclaveState.isComplete ? 'Terminé' : (conclaveState.isPaused ? 'En pause' : 'En cours')}
                </span>
              </div>
              
              {conclaveState.isPaused && (
                <div className="status-row">
                  <span className="status-label">Raison de la pause:</span>
                  <span className="status-value">{conclaveState.pauseReason}</span>
                </div>
              )}
              
              {conclaveState.isComplete && conclaveState.popeElected ? (
                <div className="elected-pope-info">
                  <h3>Pape élu:</h3>
                  <p className="pope-name">{conclaveState.popeElected.nomComplet}</p>
                </div>
              ) : (
                <>
                  <div className="status-row">
                    <span className="status-label">Jour:</span>
                    <span className="status-value">{conclaveState.currentDay}</span>
                  </div>
                  
                  <div className="status-row">
                    <span className="status-label">Période:</span>
                    <span className="status-value">{conclaveState.dayPeriod === 'morning' ? 'Matin' : 'Après-midi'}</span>
                  </div>
                  
                  <div className="status-row">
                    <span className="status-label">Tour actuel:</span>
                    <span className="status-value">{conclaveState.currentRound} (total: {conclaveState.totalRounds})</span>
                  </div>
                  
                  <div className="status-row">
                    <span className="status-label">Majorité requise:</span>
                    <span className="status-value">
                      {conclaveState.requiredMajority} votes 
                      ({Math.round((conclaveState.requiredMajority / conclaveState.electors) * 100)}%)
                    </span>
                  </div>
                  
                  <div className="status-row">
                    <span className="status-label">Fumée:</span>
                    <span className={`status-value ${conclaveState.whiteSmoke ? 'white-smoke' : 'black-smoke'}`}>
                      {conclaveState.whiteSmoke ? 'Blanche' : 'Noire'}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
          
          {!editMode && !conclaveState.isComplete && (
            <div className="control-buttons">
              <button 
                className="next-round-button"
                onClick={advanceToNextRound}
                disabled={conclaveState.isComplete}
                title="Passer au tour suivant"
              >
                <IconNextRound /> Tour suivant
              </button>
              
              {conclaveState.isPaused ? (
                <button 
                  className="resume-button"
                  onClick={resumeConclave}
                  title="Reprendre le conclave"
                >
                  <IconResume /> Reprendre
                </button>
              ) : (
                <button 
                  className="pause-button"
                  onClick={pauseConclave}
                  title="Mettre en pause le conclave"
                >
                  <IconPause /> Pause
                </button>
              )}
              
              <button 
                className="reset-all-button"
                onClick={resetConclave}
                title="Réinitialiser tout le conclave"
              >
                <IconReset /> Réinitialiser tout
              </button>
            </div>
          )}
        </div>
        
        <div className="votes-panel">
          <h2>Votes du Tour {conclaveState.currentRound}</h2>
          
          <div className="votes-table-container">
            <table className="votes-table">
              <thead>
                <tr>
                  <th>Cardinal</th>
                  <th>Pays</th>
                  <th>Continent</th>
                  <th>Votes</th>
                  <th>Pourcentage</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentVotes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">Aucun vote pour le moment</td>
                  </tr>
                ) : (
                  currentVotes.map(vote => (
                    <tr key={vote.cardinal._id}>
                      <td>{vote.cardinal.nomComplet}</td>
                      <td>{vote.cardinal.paysService}</td>
                      <td>{vote.cardinal.continent}</td>
                      <td>{vote.votes}</td>
                      <td>{vote.percentage}%</td>
                      <td>
                        {!conclaveState.isComplete && vote.votes >= conclaveState.requiredMajority && (
                          <button 
                            className="elect-pope-button"
                            onClick={() => electPope(vote.cardinal._id)}
                            title="Élire comme pape"
                          >
                            <IconCrown /> Élire
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <div className="admin-panels">
        <div className="rounds-panel">
          <h2>Historique des Tours</h2>
          
          <div className="rounds-table-container">
            <table className="rounds-table">
              <thead>
                <tr>
                  <th>Tour</th>
                  <th>Jour / Période</th>
                  <th>Votes</th>
                  <th>Votants uniques</th>
                  <th>État</th>
                  <th>Top 3</th>
                </tr>
              </thead>
              <tbody>
                {rounds.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">Aucun tour enregistré</td>
                  </tr>
                ) : (
                  rounds.map(round => (
                    <tr key={round._id} className={round.roundNumber === conclaveState.currentRound ? 'current-round' : ''}>
                      <td>{round.roundNumber}</td>
                      <td>
                        Jour {round.day} ({round.period === 'morning' ? 'Matin' : 'Après-midi'})
                      </td>
                      <td>{round.totalVotes}</td>
                      <td>{round.uniqueVoters}</td>
                      <td>
                        <span className={`status-badge ${round.isComplete ? (round.whiteSmoke ? 'white-smoke' : 'black-smoke') : 'active'}`}>
                          {round.isComplete 
                            ? (round.whiteSmoke ? 'Fumée blanche' : 'Fumée noire') 
                            : 'En cours'}
                        </span>
                      </td>
                      <td>
                        {round.results && round.results.length > 0 ? (
                          <div className="round-top-3">
                            {round.results.slice(0, 3).map((result, idx) => (
                              <div key={result.cardinalId} className="top-3-item">
                                <span className="rank">{idx + 1}.</span>
                                <span className="name">{result.cardinalId?.nomComplet}</span>
                                <span className="votes">({result.votes} votes)</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          'Pas encore de résultats'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConclaveAdminPage;